/**
 * ServicePayments — inline payments timeline + record-form for a single
 * tour service row (hotel / activity / transport / guide).
 *
 * Designed to live under each service card so the operator can see every
 * payment with its date and add a new one in two taps. Reads from
 * `GET /api/pollen/payments?tour_id=&category=&vendor_id=` (vendor_id is
 * optional; we fall back to client-side filtering by vendor_name).
 *
 * Recording a payment goes through the same `POST /api/pollen/payments`
 * endpoint used by the Dashboard's Vendor Payables card — so a payment
 * entered here shows up there and vice-versa.
 */
import React, { useEffect, useState } from 'react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, ChevronDown, ChevronUp, Loader2, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';

const authHdrs = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const ServicePayments = ({ tourId, category, vendorName, vendorId, defaultAmount, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [amount, setAmount] = useState(String(defaultAmount || ''));
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    if (!tourId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ tour_id: tourId, category });
      if (vendorId) params.set('vendor_id', vendorId);
      const res = await fetch(`${API}/pollen/payments?${params}`, { credentials: 'include', headers: authHdrs() });
      if (res.ok) {
        let rows = await res.json();
        // When we don't have a vendor_id we fall back to matching by name
        // so payments recorded from the dashboard (which always stamps the
        // vendor_name) still show up here.
        if (!vendorId && vendorName) {
          const wanted = vendorName.trim().toLowerCase();
          rows = rows.filter(p => (p.vendor_name || '').trim().toLowerCase() === wanted);
        }
        setPayments(rows);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) reload(); /* eslint-disable-next-line */ }, [open, tourId, category, vendorId, vendorName]);

  const submitPay = async () => {
    if (!amount || Number(amount) <= 0) { toast.error('Enter a valid amount'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/pollen/payments`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHdrs() },
        body: JSON.stringify({
          tour_id: tourId, vendor_id: vendorId || '', vendor_name: vendorName,
          category, amount: Number(amount), date, notes,
        }),
      });
      if (!res.ok) { toast.error('Could not save payment'); return; }
      toast.success(`Recorded ${fmtMoney(amount)} to ${vendorName}`);
      setAdding(false); setAmount(''); setNotes('');
      reload();
      onChanged && onChanged();
    } finally { setSaving(false); }
  };

  const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);

  return (
    <div className="text-xs" data-testid={`service-payments-${category}-${tourId}`}>
      <button type="button" onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
        data-testid={`payments-toggle-${category}`}>
        <IndianRupee className="w-3 h-3" />
        <span>Payments</span>
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className="mt-1.5 rounded-md border bg-muted/20 p-2 space-y-1.5">
          {loading && <div className="text-[11px] text-muted-foreground">Loading…</div>}
          {!loading && payments.length === 0 && !adding && (
            <div className="text-[11px] text-muted-foreground">No payments recorded yet.</div>
          )}
          {!loading && payments.map(p => (
            <div key={p.id} className="flex items-center justify-between text-[11px] gap-2">
              <span className="text-muted-foreground tabular-nums shrink-0">
                {(p.date || p.created_at || '').slice(0, 10)}
              </span>
              <span className="flex-1 truncate text-foreground/80">{p.notes || '—'}</span>
              <span className="font-semibold tabular-nums text-emerald-700">{fmtMoney(p.amount)}</span>
            </div>
          ))}
          {!loading && payments.length > 0 && (
            <div className="flex items-center justify-between text-[10px] pt-1 border-t text-muted-foreground">
              <span>{payments.length} payment{payments.length === 1 ? '' : 's'}</span>
              <span className="font-semibold text-emerald-700">Total: {fmtMoney(totalPaid)}</span>
            </div>
          )}
          {!adding ? (
            <Button size="sm" variant="outline" className="w-full h-7 text-[11px] mt-1"
              onClick={() => setAdding(true)} data-testid={`add-payment-${category}`}>
              <Plus className="w-3 h-3 mr-1" />Add payment
            </Button>
          ) : (
            <div className="flex flex-wrap items-end gap-1.5 pt-1 border-t">
              <div className="flex-1 min-w-[80px]">
                <label className="text-[9px] uppercase tracking-wide text-muted-foreground">Amount</label>
                <Input type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)}
                  className="h-7 text-xs" data-testid={`pay-amount-${category}`} />
              </div>
              <div>
                <label className="text-[9px] uppercase tracking-wide text-muted-foreground">Date</label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className="h-7 text-xs w-[120px]" data-testid={`pay-date-${category}`} />
              </div>
              <div className="basis-full">
                <label className="text-[9px] uppercase tracking-wide text-muted-foreground">Notes</label>
                <Input value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="UTR / mode…" className="h-7 text-xs"
                  data-testid={`pay-notes-${category}`} />
              </div>
              <Button size="sm" className="h-7 text-[11px]" disabled={saving}
                onClick={submitPay} data-testid={`pay-submit-${category}`}>
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Record'}
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-[11px]"
                onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ServicePayments;
